import React, { useEffect, useMemo, useState } from "react";
import { Search, Plus, Eye, RefreshCw, LifeBuoy, Send, CheckCircle2 } from "lucide-react";
import { toast } from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";
import {
  supportTicketAPI,
  SupportTicketListItem,
  SupportTicketDetail,
  SupportTicketStatusId,
} from "../api/axiosClient";


const STATUS: Record<"Open" | "InProgress" | "WaitingUser" | "Closed", SupportTicketStatusId> = {
  Open: 1,
  InProgress: 2,
  WaitingUser: 3,
  Closed: 4,
} as const;

type Tone = "gray" | "purple" | "green" | "yellow" | "red";

const STATUS_LABEL: Record<number, string> = {
  [STATUS.Open]: "Açık",
  [STATUS.InProgress]: "İşlemde",
  [STATUS.WaitingUser]: "Kullanıcıdan Yanıt Bekleniyor",
  [STATUS.Closed]: "Kapalı",
};

const StatusSwitch: React.FC<{
  value?: SupportTicketStatusId;
  onChange: (v: SupportTicketStatusId) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
  const options: Array<{ id: SupportTicketStatusId; label: string }> = [
    { id: STATUS.InProgress, label: "İşlemde" },
    { id: STATUS.WaitingUser, label: "Kullanıcıdan Yanıt Bekleniyor" },
    { id: STATUS.Closed, label: "Kapalı" },
  ];

  return (
    <div className={`inline-flex rounded-xl border border-gray-200 p-1 bg-gray-50 ${disabled ? "opacity-60 pointer-events-none" : ""}`}>
      {options.map((opt) => {
        const active = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            className={[
              "h-10 px-4 rounded-lg text-sm font-medium transition",
              active ? "bg-[#9B51E0] text-white shadow-sm" : "text-gray-700 hover:bg-white",
            ].join(" ")}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
};



const statusLabel = (t: { statusId?: number; status?: string }) =>
  STATUS_LABEL[Number(t.statusId)] ?? "-";

const statusToneById = (id?: number): Tone => {
  switch (Number(id)) {
    case STATUS.Open:
      return "yellow";
    case STATUS.InProgress:
      return "purple";
    case STATUS.WaitingUser:
      return "red"; // istersen yellow da yapabilirsin
    case STATUS.Closed:
      return "green";
    default:
      return "gray";
  }
};
const extractList = (resp: any): SupportTicketListItem[] => {
  const items = resp?.data?.items ?? [];
  return Array.isArray(items) ? items : [];
};

const Badge: React.FC<{ children: React.ReactNode; tone?: "gray" | "purple" | "green" | "yellow" | "red" }> = ({
  children,
  tone = "gray",
}) => {
  const map = {
    gray: "bg-gray-100 text-gray-700 border-gray-200",
    purple: "bg-purple-50 text-[#9B51E0] border-purple-200",
    green: "bg-green-50 text-green-700 border-green-200",
    yellow: "bg-yellow-50 text-yellow-800 border-yellow-200",
    red: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium border rounded-full ${map[tone]}`}>
      {children}
    </span>
  );
};

type TabKey = "create" | "my" | "all";

const HelpSupportPage: React.FC = () => {
  const { user } = useAuth();
  const isManager = user?.role === "SchoolManager";

  const [tab, setTab] = useState<TabKey>("my");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<SupportTicketListItem[]>([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<SupportTicketDetail | null>(null);

  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");

  const [category, setCategory] = useState("Genel");
  const [priority, setPriority] = useState("Medium");

  const extractList = (resp: any): SupportTicketListItem[] => {
    const list = resp?.data?.items ?? resp?.data ?? [];
    return Array.isArray(list) ? list : [];
  };

  const load = async (mode: TabKey) => {
    setLoading(true);
    try {
      if (mode === "my") {
        const res = await supportTicketAPI.listMy();
        if (!res?.success) throw new Error(res?.message || "List error");
        setItems(extractList(res));
      } else if (mode === "all") {
        const res = await supportTicketAPI.listAll();
        if (!res?.success) throw new Error(res?.message || "List error");
        setItems(extractList(res));
      } else {
        setItems([]);
      }
    } catch (e) {
      console.error(e);
      toast.error("Talepler yüklenemedi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "my" || tab === "all") load(tab);
  }, [tab]);

    const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();

    return items.filter((t) => {
        const okQ =
        !query ||
        t.subject?.toLowerCase().includes(query) ||
        (t.ticketNo ?? "").toLowerCase().includes(query) ||
        statusLabel(t).toLowerCase().includes(query);

        const okS = !statusFilter || String(t.statusId) === statusFilter;

        return okQ && okS;
    });
    }, [items, q, statusFilter]);


  const openDetail = async (ticketId: string) => {
    setSelectedId(ticketId);
    setDetailOpen(true);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await supportTicketAPI.getById(ticketId);
      if (!res?.success) throw new Error(res?.message || "Detail error");
      setDetail((res.data as any) ?? null);
    } catch (e) {
      console.error(e);
      toast.error("Detay yüklenemedi.");
    } finally {
      setDetailLoading(false);
    }
  };

  const submit = async () => {
    if (!subject.trim() || !description.trim()) {
      toast.error("Başlık ve açıklama zorunlu.");
      return;
    }

    try {
      const res = await supportTicketAPI.create({
        subject: subject.trim(),
        description: description.trim(),
      });

      toast.success("Destek talebi oluşturuldu.");
      setSubject("");
      setDescription("");
      setCategory("Genel");
      setPriority("Medium");
      setTab("my");
      await load("my");
    } catch (e) {
      console.error(e);
      toast.error("Talep oluşturulamadı.");
    }
  };

  const updateStatus = async (newStatus: SupportTicketStatusId) => {
    if (!selectedId) return;
    try {
      const res = await supportTicketAPI.updateStatus(selectedId, newStatus);
      if (!res?.success) {
        toast.error(res?.message || "Durum güncellenemedi.");
        return;
      }

      toast.success("Durum güncellendi.");

      const dRes = await supportTicketAPI.getById(selectedId);
      if (dRes?.success) setDetail((dRes.data as any) ?? null);

      if (tab === "my" || tab === "all") await load(tab);
    } catch (e) {
      console.error(e);
      toast.error("Durum güncellenemedi.");
    }
  };

const stats = useMemo(() => {
  const open = items.filter((i) => Number(i.statusId) === STATUS.Open).length;
  const progress = items.filter((i) => Number(i.statusId) === STATUS.InProgress).length;
  const waiting = items.filter((i) => Number(i.statusId) === STATUS.WaitingUser).length;
  const closed = items.filter((i) => Number(i.statusId) === STATUS.Closed).length;

  return {
    total: items.length,
    open,
    progress,
    waiting,
    closed,
    done: closed, 
  };
}, [items]);


  const TabButton: React.FC<{ k: TabKey; label: string; icon?: React.ReactNode }> = ({ k, label, icon }) => {
    const active = tab === k;
    return (
      <button
        onClick={() => setTab(k)}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition
          ${active ? "bg-[#9B51E0] text-white border-[#9B51E0]" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"}`}
      >
        {icon}
        {label}
      </button>
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <LifeBuoy className="w-6 h-6 text-[#9B51E0]" />
            <h1 className="text-2xl font-bold text-gray-900">Yardım & Destek</h1>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Destek talebi oluşturabilir, taleplerinizi takip edebilir ve çözümleri görüntüleyebilirsiniz.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <TabButton k="create" label="Yeni Talep" icon={<Plus className="w-4 h-4" />} />
          <TabButton k="my" label="Taleplerim" />
          {isManager && <TabButton k="all" label="Tüm Talepler" />}
        </div>
      </div>

      {/* Stats */}
    {(tab === "my" || tab === "all") && (
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-sm text-gray-500">Toplam</div>
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-sm text-gray-500">Açık</div>
          <div className="text-2xl font-bold text-gray-900">{stats.open}</div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-sm text-gray-500">İşlemde</div>
          <div className="text-2xl font-bold text-gray-900">{stats.progress}</div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-sm text-gray-500">Yanıt Bekliyor</div>
          <div className="text-2xl font-bold text-gray-900">{stats.waiting}</div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-sm text-gray-500">Çözülen</div>
          <div className="text-2xl font-bold text-gray-900">{stats.done}</div>
        </div>
      </div>
    )}


      {/* Create */}
      {tab === "create" && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Yeni Destek Talebi</h2>
            <Badge tone="purple">Yanıt süresi: çalışma saatlerinde daha hızlı</Badge>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Başlık</label>
              <input
                className="w-full h-11 px-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-200"
                placeholder="Örn: Etkinlik listesi görünmüyor"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            {/* UI'da kalsın (Swagger'da yok) */}
{/*             <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kategori</label>
                <select
                  className="w-full h-11 px-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-200"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option>Genel</option>
                  <option>Teknik</option>
                  <option>Ödeme</option>
                  <option>Etkinlik</option>
                  <option>Diğer</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Öncelik</label>
                <select
                  className="w-full h-11 px-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-200"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
              </div>
            </div> */}

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
              <textarea
                className="w-full min-h-[140px] p-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-200"
                placeholder="Sorunu adım adım anlat (varsa ekran görüntüsü, tarih, kullanıcı bilgisi vs.)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <div className="text-xs text-gray-500 mt-2">
                İpucu: “Ne yaptım / Ne bekliyordum / Ne oldu” formatı en hızlı çözümdür.
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end mt-6 gap-2">
            <button
              className="h-11 px-4 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
              onClick={() => {
                setSubject("");
                setDescription("");
                setCategory("Genel");
                setPriority("Medium");
              }}
            >
              Temizle
            </button>

            <button className="h-11 px-4 rounded-lg bg-[#9B51E0] text-white hover:opacity-95 flex items-center gap-2" onClick={submit}>
              <Send className="w-4 h-4" />
              Gönder
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {(tab === "my" || tab === "all") && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <div className="relative w-full md:w-[360px]">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  className="w-full h-11 pl-9 pr-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-200"
                  placeholder="Talep ara (başlık/durum)..."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>

              <select
                className="h-11 px-3 rounded-lg border border-gray-200 text-sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">Tüm durumlar</option>
                <option value={String(STATUS.Open)}>Açık</option>
                <option value={String(STATUS.InProgress)}>İşlemde</option>
                <option value={String(STATUS.WaitingUser)}>Kullanıcıdan Yanıt Bekleniyor</option>
                <option value={String(STATUS.Closed)}>Kapalı</option>
              </select>
            </div>

            <button
              className="h-11 px-4 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              onClick={() => load(tab)}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Yenile
            </button>
          </div>

          <div className="overflow-x-auto border border-gray-100 rounded-xl">
            <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
            <tr>
                <th className="text-left font-semibold px-4 py-3">Konu</th>
                <th className="text-left font-semibold px-4 py-3">Atanan</th>
                <th className="text-left font-semibold px-4 py-3">Durum</th>
                <th className="text-left font-semibold px-4 py-3">Oluşturma</th>
                <th className="text-right font-semibold px-4 py-3">İşlem</th>
            </tr>
            </thead>


              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-4 py-3"><div className="h-4 bg-gray-100 rounded w-48" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-100 rounded w-24" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-100 rounded w-20" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-100 rounded w-24" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-100 rounded w-28" /></td>
                      <td className="px-4 py-3"><div className="h-8 bg-gray-100 rounded w-16 ml-auto" /></td>
                    </tr>
                  ))
                ) : filtered.length === 0 ? (
                  <tr>
                    <td className="px-4 py-10 text-center text-gray-500" colSpan={6}>
                      Kayıt bulunamadı.
                    </td>
                  </tr>
                ) : (
                filtered.map((t) => (
                <tr key={t.ticketId} className="hover:bg-gray-50">
                    {/* Konu */}
                    <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{t.subject}</div>
                    <div className="text-xs text-gray-500">{t.ticketNo ?? t.ticketId}</div>
                    </td>

                    {/* Atanan */}
                    <td className="px-4 py-3">
                    <span className="text-gray-700">
                        {(t.assignedUser ?? "").trim() ? t.assignedUser : "-"}
                    </span>
                    </td>

                    {/* Durum */}
                    <td className="px-4 py-3">
                    <Badge tone={statusToneById(t.statusId)}>{statusLabel(t)}</Badge>
                    </td>

                    {/* Oluşturma */}
                    <td className="px-4 py-3">
                    {t.createdAt ? new Date(t.createdAt).toLocaleString() : "-"}
                    </td>

                    {/* İşlem */}
                    <td className="px-4 py-3 text-right">
                    <button
                        type="button"
                        className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-gray-200 hover:bg-white"
                        onClick={() => openDetail(t.ticketId)}
                    >
                        <Eye className="w-4 h-4" />
                        Gör
                    </button>
                    </td>
                </tr>
                ))


                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detailOpen && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-50" onClick={() => setDetailOpen(false)}>
          <div className="w-full max-w-2xl bg-white rounded-2xl border border-gray-200 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-200 flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-500">Destek Talebi</div>
                <div className="text-lg font-semibold text-gray-900">{detail?.subject ?? "Detay"}</div>
              </div>
              <button className="h-10 px-3 rounded-lg border border-gray-200 hover:bg-gray-50" onClick={() => setDetailOpen(false)}>
                Kapat
              </button>
            </div>

            <div className="p-5">
              {detailLoading ? (
                <div className="space-y-3 animate-pulse">
                  <div className="h-4 bg-gray-100 rounded w-2/3" />
                  <div className="h-4 bg-gray-100 rounded w-1/2" />
                  <div className="h-20 bg-gray-100 rounded w-full" />
                </div>
              ) : !detail ? (
                <div className="text-sm text-gray-600">Detay bulunamadı.</div>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Badge tone={statusToneById(detail.statusId) as any}>{statusLabel(detail)}</Badge>
                  </div>

                  <div className="text-sm text-gray-700 whitespace-pre-wrap border border-gray-200 rounded-xl p-4 bg-gray-50">
                    {detail.description ?? "-"}
                  </div>

                  <div className="mt-4 text-xs text-gray-500">
                    Oluşturma: {detail.createdDate ? new Date(detail.createdDate).toLocaleString() : "-"}
                  </div>

                  {isManager && (
                    <div className="mt-5 border-t border-gray-200 pt-4">
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-semibold text-gray-900">Durum Güncelle</div>

                        <StatusSwitch
                          value={detail?.statusId as SupportTicketStatusId | undefined}
                          onChange={(v) => {
                            if ((detail?.statusId as SupportTicketStatusId | undefined) === v) return;
                            updateStatus(v); // ✅ v artık SupportTicketStatusId
                          }}
                          />
                      </div>
                    </div>
                  )}

                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpSupportPage;
